import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { setAllApiTokens } from '@/services/apiTokenManager';

export type AdminRole = 'ADMIN' | 'CONTENT_MANAGER' | 'USER';

export interface AdminUser {
  id: string;
  username: string;
  role: AdminRole;
  /** Raw role names from identity service (e.g. ['SUPER_ADMIN'], ['ADMIN'], ['USER']) */
  roles?: string[];
  email?: string;
  accessToken?: string;
}

interface AdminAuthContextValue {
  user: AdminUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (
    permission: 'view' | 'create' | 'edit' | 'delete' | 'publish' | 'archive' | 'activate' | 'deactivate' | 'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate' | string,
    entity: 'content' | 'survey' | 'skill' | 'occupation' | 'win' | 'painPoint' | 'subscriptionPlan' | 'affiliatorGroup' | 'notificationTemplate' | 'permission' | string
  ) => boolean;
  hasRole: (role: string) => boolean;
  /** True only when the current admin has SUPER_ADMIN role (e.g. can update user roles). */
  isSuperAdmin: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(undefined);

const ADMIN_TOKEN_KEY = 'admin_access_token';
const ADMIN_USER_KEY = 'admin_user';

function resolveAdminRole(roles: string[]): AdminRole {
  if (!roles || roles.length === 0) return 'USER';
  const upper = roles.map((r) => r.toUpperCase());
  if (upper.some((r) => r === 'SUPER_ADMIN' || r === 'ADMIN')) return 'ADMIN';
  if (upper.some((r) => r === 'CONTENT_MANAGER')) return 'CONTENT_MANAGER';
  return 'USER';
}

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(() => {
    try {
      const stored = localStorage.getItem(ADMIN_USER_KEY);
      const parsed: AdminUser | null = stored ? JSON.parse(stored) : null;
      // Set tokens synchronously BEFORE any child component renders so React Query
      // doesn't fire its first fetch with a null token (race condition on page refresh).
      if (parsed?.accessToken) {
        setAllApiTokens(parsed.accessToken);
      }
      return parsed;
    } catch {
      return null;
    }
  });

  // Keep tokens in sync whenever user state changes (login / logout / token rotation).
  useEffect(() => {
    if (user) {
      localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(user));
      if (user.accessToken) {
        setAllApiTokens(user.accessToken);
      }
    } else {
      localStorage.removeItem(ADMIN_USER_KEY);
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      setAllApiTokens(null);
    }
  }, [user]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/user-identity/api/v1/auth/admin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        mode: 'cors',
      });

      if (!response.ok) {
        return false;
      }

      const body = await response.json();
      // Response is ApiResponse<AuthenticationResponse> → { success, data: { accessToken, user, ... } }
      const authData = body?.data ?? body;

      const accessToken: string | undefined = authData?.accessToken;
      if (!accessToken) return false;

      const userData = authData?.user;
      const roles: string[] = userData?.roles ?? [];

      const adminUser: AdminUser = {
        id: userData?.id ?? email,
        username: userData?.username ?? email,
        role: resolveAdminRole(roles),
        roles: Array.isArray(roles) ? roles.map((r: string) => String(r).toUpperCase()) : [],
        email: userData?.email ?? email,
        accessToken,
      };

      setAllApiTokens(accessToken);
      setUser(adminUser);
      return true;
    } catch (err) {
      console.error('[AdminAuth] Login error:', err);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
  };

  const hasPermission = (
    permission: 'view' | 'create' | 'edit' | 'delete' | 'publish' | 'archive' | 'activate' | 'deactivate' | 'approve' | 'reject' | 'suspend' | 'reactivate' | 'terminate' | string,
    entity: 'content' | 'survey' | 'skill' | 'occupation' | 'win' | 'painPoint' | 'subscriptionPlan' | 'affiliatorGroup' | 'notificationTemplate' | 'permission' | string
  ): boolean => {
    if (!user) return false;

    // ADMIN has all permissions
    if (user.role === 'ADMIN') return true;

    // Check for specific permission strings (e.g., 'READ_PERMISSION', 'CREATE_PERMISSION', 'SYSTEM_ADMIN')
    if (typeof permission === 'string' && permission.includes('_')) {
      if (user.role === 'CONTENT_MANAGER') {
        return ['READ_PERMISSION', 'CREATE_PERMISSION', 'UPDATE_PERMISSION'].includes(permission);
      }
      return false;
    }

    // CONTENT_MANAGER permissions
    if (user.role === 'CONTENT_MANAGER') {
      if (entity === 'content') {
        return ['view', 'create', 'edit'].includes(permission);
      }
      if (entity === 'survey') {
        return ['view', 'create', 'edit', 'publish'].includes(permission);
      }
      if (['skill', 'occupation', 'win', 'painPoint'].includes(entity)) {
        return ['view', 'create', 'edit', 'delete', 'activate', 'deactivate'].includes(permission);
      }
      if (entity === 'affiliatorGroup') {
        return ['view', 'create', 'edit', 'delete', 'approve', 'reject', 'suspend', 'reactivate', 'terminate'].includes(permission);
      }
      if (entity === 'notificationTemplate') {
        return ['view', 'create', 'edit'].includes(permission);
      }
    }

    // USER permissions - read-only
    if (user.role === 'USER') {
      if (entity === 'content') return ['view', 'create'].includes(permission);
      if (entity === 'survey') return permission === 'view';
      if (entity === 'subscriptionPlan') return permission === 'view';
      if (['skill', 'occupation', 'win', 'painPoint'].includes(entity)) return permission === 'view';
      if (entity === 'notificationTemplate') return permission === 'view';
      if (entity === 'affiliatorGroup') return false;
      return permission === 'view';
    }

    return false;
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    const upper = role.toUpperCase();
    if (user.roles?.includes(upper)) return true;
    if (user.role === 'ADMIN') return ['ADMIN', 'SYSTEM', 'SYSTEM_ADMIN'].includes(upper);
    if (user.role === 'CONTENT_MANAGER') return upper === 'ADMIN';
    return false;
  };

  const isSuperAdmin = Boolean(user?.roles?.includes('SUPER_ADMIN'));

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        hasPermission,
        hasRole,
        isSuperAdmin,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

// useAdminAuth is the public hook for AdminAuthContext
/* eslint-disable-next-line react-refresh/only-export-components */
export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
